import { useEffect } from 'react';
import { onNotificationFrame } from '../hooks/useNotificationEventStream';
import { notifyPostingOutcome } from '../lib/notify';

/**
 * N8 (86d3w2kez) part 3 — turns arriving notification frames into toasts.
 *
 * ⚠️ MOUNT EXACTLY ONCE, in `AppLayout`, beside the stream itself. Two mounts would each toast every
 * frame, and the update-in-place key would not save them: both would write the same toast id, so the
 * user would see one toast being fought over rather than two, which is a stranger bug to diagnose than
 * plain duplicates.
 *
 * <b>It listens to the FRAME fan-out, not the store.</b> The store answers "what is true now"; a toast
 * is about "something just arrived", and those are different questions. Reconstructing an arrival by
 * diffing store snapshots is both fiddly and wrong across a batched frame — which is exactly why N5
 * shipped `onNotificationFrame` alongside the store rather than instead of it.
 */
export function PostingOutcomeToaster() {
  useEffect(
    () =>
      onNotificationFrame((frame) => {
        switch (frame.type) {
          case 'upsert':
            notifyPostingOutcome(frame.notification);
            return;

          case 'notifications.changed':
            // A batched frame is the reconciler repairing many rows for one user at once. Each row is
            // still its own outcome, and `notify` keys per job — so a batch that touches one job
            // twice updates one toast rather than stacking two.
            for (const n of frame.notifications ?? []) notifyPostingOutcome(n);
            return;

          default:
            // `counts_changed` carries no rows, and an unknown frame type is one the server shipped
            // ahead of this client. Neither is an outcome.
            return;
        }
      }),
    [],
  );

  return null;
}
